import React, { useState, useMemo } from "react";
import { Box, Button, Typography, styled } from "@mui/material";
import { Link } from "react-router-dom";
import { useGetAgentsQuery } from "../../redux/api/agentsApi";
import AgentsTable from "./AgentsTable";
import { useSelector } from "react-redux";
import { selectCurrentagency } from "../../redux/features/authSlice";
import { ADD_AGENT_ROUTE } from "../../constants/routes";
import NavigationTabs from "./NavigationTabs";

export default function Agents() {
  const currentAgency = useSelector(selectCurrentagency);
  const {
    data: agents,
    isFetching,
    isSuccess,
  } = useGetAgentsQuery(currentAgency.agencyid);

  // 🔹 Filter state: 1 = Active (default), 0 = Inactive, "all" = all
  const [filterStatus, setFilterStatus] = useState(1);

  // 🔹 Filtered agents data (computed only when data or filter changes)
  const filteredAgents = useMemo(() => {
    if (!agents) return [];
    if (filterStatus === "all") return agents;
    return agents.filter((agent) => agent.status === filterStatus);
  }, [agents, filterStatus]);

  return (
    <Box>
      {/* Header */}
      <StyledTop>
        <Box>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "1.6rem", sm: "2rem", md: "2.25rem" },
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            Staff
            {isSuccess && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  fontWeight: 500,
                  color: "gray",
                }}
              >
                ({filteredAgents.length} total)
              </Typography>
            )}
          </Typography>
        </Box>

        {/* Tabs + Add Button */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            alignItems: "center",
            justifyContent: { xs: "flex-start", sm: "flex-end" },
            width: { xs: "100%", md: "auto" },
          }}
        >
          <NavigationTabs />

          <Button
            component={Link}
            to={ADD_AGENT_ROUTE}
            variant="contained"
            sx={{
              color: "white",
              fontWeight: 600,
              borderRadius: 2,
              px: { xs: 2, sm: 2.5 },
              py: { xs: 1, sm: 1.1 },
              background: "#56C662",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            Add Staff
          </Button>
        </Box>
      </StyledTop>

      {/* 🔹 Filter Buttons */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <Button
          variant={filterStatus === 1 ? "contained" : "outlined"}
          onClick={() => setFilterStatus(1)}
          sx={{ fontWeight: 600, borderRadius: 2, color: filterStatus === 1 ? "white" : "" }}
        >
          Active Agents
        </Button>

        <Button
          variant={filterStatus === 0 ? "contained" : "outlined"}
          color="warning"
          onClick={() => setFilterStatus(0)}
          sx={{ fontWeight: 600, borderRadius: 2 }}
        >
          Inactive Agents
        </Button>

        <Button
          variant={filterStatus === "all" ? "contained" : "outlined"}
          color="primary"
          onClick={() => setFilterStatus("all")}
          sx={{ fontWeight: 600, borderRadius: 2, color: filterStatus === "all" ? "white" : "" }}
        >
          All Agents
        </Button>
      </Box>

      {/* Table */}
      <AgentsTable
        isFetching={isFetching}
        isSuccess={isSuccess}
        data={filteredAgents}
      />
    </Box>
  );
}

const StyledTop = styled("div")(({ theme }) => ({
  marginTop: 16,
  marginBottom: 16,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    alignItems: "flex-start",
  },
}));
